import {
  Box,
  Button,
  Flex,
  Heading,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Icon,
  Divider,
  SlideFade
} from "@chakra-ui/react";
import { useState, useContext } from "react";
import { COLORS } from "../../constants/colors";
import { createEmergency } from "./service/createEmergency";
import { UserProfileContext } from "../../context/userProfileContext";
import {
  BsTelephoneFill,
  BsHeartFill,
  BsShieldShaded,
  BsThreeDotsVertical
} from "react-icons/bs";
import { FaFire, FaExclamationTriangle } from "react-icons/fa";
import Container from "../../components/Container";
import Header from "../../components/Header";
import Footer from "../../components/footer";
import CustomModal from "../../components/CustomModal";
import { Textarea } from "@chakra-ui/react";
import { transition } from "../..";

const LAGOS_EMERGENCY_NUMBERS = [
  { name: "Lagos State Emergency", number: "767" },
  { name: "Lagos State Emergency (Alt)", number: "112" },
  { name: "Nigeria Police", number: "08032003913" },
  { name: "Fire Service", number: "08033265481" },
  { name: "Ambulance Service", number: "08022225666" },
  { name: "FRSC (Road Safety)", number: "122" },
  { name: "NEMA (Disaster)", number: "080022636432" }
];

const EMERGENCY_TYPES = [
  { key: "Health", label: "Health", icon: BsHeartFill, color: "red.400" },
  { key: "Fire", label: "Fire", icon: FaFire, color: "orange.400" },
  { key: "Theft", label: "Theft", icon: BsShieldShaded, color: "blue.400" },
  {
    key: "Others",
    label: "Others",
    icon: BsThreeDotsVertical,
    color: COLORS.primary
  }
];

const EmergencyDashboard = () => {
  const { userProfile } = useContext(UserProfileContext);
  const [loadingType, setLoadingType] = useState("");
  const [otherDesc, setOtherDesc] = useState("");
  const [otherLoading, setOtherLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [isOtherOpen, setIsOtherOpen] = useState(false);
  const cardBg = useColorModeValue(COLORS.white, COLORS.primary);
  const pageBg = useColorModeValue(COLORS.bgGrey, COLORS.darkModeBg);

  const textColor = useColorModeValue(COLORS.primary, COLORS.white);
  const emergencyNumberBg = useColorModeValue("gray.50", "gray.700");
  const textareaBg = useColorModeValue("gray.50", "gray.700");

  const handleQuickEmergency = async (type) => {
    setLoadingType(type);
    setSuccessMsg("");
    await createEmergency(
      {
        tenantCode: userProfile?.tenantCode,
        userId: userProfile?._id,
        type,
        description: type + " emergency triggered"
      },
      () => {},
      () => setSuccessMsg(`${type} emergency raised!`)
    );
    setLoadingType("");
  };

  const handleOtherEmergency = async () => {
    if (!otherDesc.trim()) return;
    setOtherLoading(true);
    setSuccessMsg("");
    await createEmergency(
      {
        tenantCode: userProfile?.tenantCode,
        userId: userProfile?._id,
        type: "Others",
        description: otherDesc
      },
      () => {},
      () => setSuccessMsg("Other emergency raised!")
    );
    setOtherLoading(false);
    setOtherDesc("");
    setIsOtherOpen(false);
  };

  return (
    <SlideFade in={true} offsetY="500px" {...transition}>
      <Stack pb="15vh" w={"100%"} overflowX={"hidden"}>
        <Stack bg={pageBg}>
          <Container>
            <Header menu />
            <Flex direction="column" align="center" mt={2}>
              <FaExclamationTriangle
                fontSize={"3rem"}
                color={COLORS.primary}
                mb={2}
              />
              <Text color={textColor} textAlign="center" fontWeight="bold">
                Raise an emergency or call a national emergency number
              </Text>
              <Text color="orange.500" fontSize="sm" textAlign="center" mt={1}>
                <b>Note:</b> If you do not have an actual emergency, please do
                not click any emergency button.
              </Text>
            </Flex>
          </Container>
        </Stack>
        <Container>
          <Box py={4}>
            <Stack
              direction={["column", "row"]}
              spacing={6}
              justify="center"
              mb={8}
              align="center"
            >
              {EMERGENCY_TYPES.map(({ key, label, icon, color }) =>
                key !== "Others" ? (
                  <Button
                    key={key}
                    colorScheme={color.split(".")[0]}
                    variant="solid"
                    size="lg"
                    isLoading={loadingType === key}
                    onClick={() => handleQuickEmergency(key)}
                    w={["150px", "150px"]}
                    h="60px"
                    fontWeight="bold"
                    fontSize="lg"
                    boxShadow="md"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    px={2}
                  >
                    <Flex direction="column" align="center" justify="center">
                      {icon && <Icon as={icon} boxSize={7} mb={1} />}
                      <Text>{label}</Text>
                    </Flex>
                  </Button>
                ) : (
                  <CustomModal
                    key={key}
                    btnTitle={
                      <Flex direction="column" align="center" justify="center">
                        {icon && <Icon as={icon} boxSize={7} mb={1} />}
                        <Text>{label}</Text>
                      </Flex>
                    }
                    isOpen={isOtherOpen}
                    onOpen={() => setIsOtherOpen(true)}
                    onClose={() => setIsOtherOpen(false)}
                    buttonProps={{
                      w: ["150px", "150px"],
                      h: "60px",
                      fontWeight: "bold",
                      fontSize: "lg",
                      boxShadow: "md",
                      bg: COLORS.primary,
                      color: "#fff"
                    }}
                  >
                    <Box p={4}>
                      <Text fontWeight="bold" mb={2} color={textColor}>
                        Other Emergency
                      </Text>
                      <Textarea
                        placeholder="Describe your emergency..."
                        value={otherDesc}
                        onChange={(e) => setOtherDesc(e.target.value)}
                        mb={3}
                        rows={3}
                        bg={textareaBg}
                      />
                      <Button
                        bg={COLORS.primary}
                        color="#fff"
                        isLoading={otherLoading}
                        onClick={handleOtherEmergency}
                        w="100%"
                      >
                        Submit
                      </Button>
                    </Box>
                  </CustomModal>
                )
              )}
            </Stack>
            {successMsg && (
              <Text
                color="green.400"
                fontWeight="bold"
                mb={4}
                textAlign="center"
              >
                {successMsg}
              </Text>
            )}
            <Divider my={6} />
            <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="md">
              <Text fontWeight="bold" mb={4} color={textColor}>
                Lagos National Emergency Numbers
              </Text>
              <VStack spacing={3} align="stretch">
                {LAGOS_EMERGENCY_NUMBERS.map(({ name, number }) => (
                  <Flex
                    key={number}
                    align="center"
                    justify="space-between"
                    bg={emergencyNumberBg}
                    p={3}
                    borderRadius="md"
                  >
                    <Box>
                      <Text fontWeight="semibold" color={textColor}>
                        {name}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {number}
                      </Text>
                    </Box>
                    <Button
                      as="a"
                      href={`tel:${number}`}
                      colorScheme="teal"
                      leftIcon={<BsTelephoneFill />}
                      variant="outline"
                      size="sm"
                    >
                      Call
                    </Button>
                  </Flex>
                ))}
              </VStack>
            </Box>
          </Box>
        </Container>
        <Footer />
      </Stack>
    </SlideFade>
  );
};

export default EmergencyDashboard;
